import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MinusIcon,
  SearchIcon,
  CheckmarkIcon,
  PersonPlusIcon,
} from "@/lib/icons";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useColumnState } from "@/components/columnView/ColumnLayout";
import { cn } from "@/lib/utils";
import { formatResponsible } from "@/lib/responsibleUser";
import type { SchedulingSpace } from "./hooks/useWorkSectionData";

const TOOLTIP_UNAVAILABLE = "Tehtävä ei löydy valitusta tehtäväpohjasta";
const COL_WIDTH = "w-14";

export type SpaceInspectionField =
  | "includeMalliasennustarkastus"
  | "includeOsakohteenTarkastus"
  | "includeMestanVastaanotto"
  | "includeTyovaiheenvastaanotto"
  | "includeLiitedokumentit";

export type AvailabilityKey =
  | "malliasennus"
  | "osakohde"
  | "mestanVastaanotto"
  | "tyovaiheenvastaanotto"
  | "liitedokumentit";

export interface FieldDescriptor {
  field: SpaceInspectionField;
  label: string;
  taskName: string;
  availabilityKey: AvailabilityKey;
}

/** Column definitions drive both the header and each space row; adding a new
 *  inspection type is a one-line change here. */
export const FIELDS: FieldDescriptor[] = [
  {
    field: "includeMestanVastaanotto",
    label: "Mestan vastaanotto",
    taskName: "Mestan vastaanotto",
    availabilityKey: "mestanVastaanotto",
  },
  {
    field: "includeMalliasennustarkastus",
    label: "Malliasennus-tarkastus",
    taskName: "Malliasennustarkastus",
    availabilityKey: "malliasennus",
  },
  {
    field: "includeOsakohteenTarkastus",
    label: "Osakohteen tarkastus",
    taskName: "Osakohteen tarkastus",
    availabilityKey: "osakohde",
  },
  {
    field: "includeTyovaiheenvastaanotto",
    label: "Työvaiheen vastaanotto",
    taskName: "Työvaiheenvastaanotto",
    availabilityKey: "tyovaiheenvastaanotto",
  },
  {
    field: "includeLiitedokumentit",
    label: "Liite-dokumentit",
    taskName: "Liitedokumentit",
    availabilityKey: "liitedokumentit",
  },
];

export interface WorkSectionSpaceState {
  schedulingSpaceId: string;
  schedulingTaskIds: string[];
  plannedStart: string;
  plannedEnd: string;
  includeOsakohteenTarkastus: boolean;
  includeMalliasennustarkastus: boolean;
  includeMestanVastaanotto: boolean;
  includeTyovaiheenvastaanotto: boolean;
  includeLiitedokumentit: boolean;
  responsibleUserId?: string | null;
}

export type SpaceUpdate = { spaceId: string; field: SpaceInspectionField; value: boolean };

interface WorkSectionSpaceTreeProps {
  spaces: WorkSectionSpaceState[];
  allSchedulingSpaces: SchedulingSpace[];
  /** When provided, the tree renders a synthetic plot row at the top that
   *  owns bulk-toggle behavior, and the column header collapses to labels
   *  only. When omitted, the legacy header carries the bulk-toggle. */
  plotName?: string;
  /** Task names present for this work section in the selected template.
   *  Omit → all disabled (treated as unknown template). */
  availableAtWs?: Set<string>;
  onUpdateSpace: (updates: SpaceUpdate[]) => void;
  /** Optional controlled collapsed-spaces state. When omitted, the tree
   *  manages expansion locally (default: all expanded). */
  collapsedSpaces?: Set<string>;
  onSetCollapsedSpaces?: (
    updater: (prev: Set<string>) => Set<string>,
  ) => void;
  /** Optional keyboard-focus state. Row 0 is the header row, rows 1..N are
   *  the flattened tree rows in render order. cellIndex 0 is the name cell;
   *  cellIndex 1..5 map to FIELDS in order. */
  focusedRowIndex?: number | null;
  focusedCellIndex?: number | null;
  onFocusCell?: (row: number, cell: number) => void;
  displayOnly?: boolean;
  /** Users for the responsible dropdown. */
  responsibleUsers?: { id: string; firstName?: string | null; lastName?: string | null; email: string }[];
  /** Effective responsible by scheduling-space id (own override OR inherited).
   *  `inherited` drives the muted/italic display on descendant rows. */
  responsibleBySpaceId?: Map<string, { userId: string; inherited: boolean }>;
  /** Assign/clear a responsible for a scheduling-space row. */
  onSetSpaceResponsible?: (schedulingSpaceId: string, userId: string | null) => void;
}

export type Availability = Record<AvailabilityKey, boolean>;

export interface TreeNode {
  space: SchedulingSpace;
  children: TreeNode[];
  hasDirectTask: boolean;
  descendantTaskSpaces: string[];
}

const PLOT_SPACE_TYPES = new Set(["PLOT", "TONTTI"]);

/** Each plot is its own project — plot-level spaces must not appear in the
 *  per-work-section tila tree.  Detach them so their children become roots. */
function stripPlotSpaces(all: SchedulingSpace[]): SchedulingSpace[] {
  const plotIds = new Set(
    all.filter((s) => s.type && PLOT_SPACE_TYPES.has(s.type)).map((s) => s.id),
  );
  if (plotIds.size === 0) return all;
  return all
    .filter((s) => !plotIds.has(s.id))
    .map((s) =>
      s.parentId && plotIds.has(s.parentId) ? { ...s, parentId: null } : s,
    );
}

function sortTreeByScheduling(
  roots: TreeNode[],
  startByTaskSpace: Map<string, string>,
  endByTaskSpace: Map<string, string>,
): TreeNode[] {
  const MAX = "\uffff";

  function sortKey(node: TreeNode): { start: string; end: string } {
    const starts: string[] = [];
    const ends: string[] = [];
    if (node.hasDirectTask) {
      const s = startByTaskSpace.get(node.space.id);
      const e = endByTaskSpace.get(node.space.id);
      if (s) starts.push(s);
      if (e) ends.push(e);
    }
    for (const child of node.children) {
      const k = sortKey(child);
      if (k.start !== MAX) starts.push(k.start);
      if (k.end !== MAX) ends.push(k.end);
    }
    return {
      start: starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : MAX,
      end: ends.length ? ends.reduce((a, b) => (a < b ? a : b)) : MAX,
    };
  }

  function compare(a: TreeNode, b: TreeNode) {
    const ka = sortKey(a);
    const kb = sortKey(b);
    if (ka.start !== kb.start) return ka.start < kb.start ? -1 : 1;
    if (ka.end !== kb.end) return ka.end < kb.end ? -1 : 1;
    return 0;
  }

  function sortNode(node: TreeNode) {
    node.children.forEach(sortNode);
    node.children.sort(compare);
  }

  roots.forEach(sortNode);
  roots.sort(compare);
  return roots;
}

function buildVisibleTree(
  taskSpaceIds: Set<string>,
  allSpaces: SchedulingSpace[],
): TreeNode[] {
  const spaceById = new Map(allSpaces.map((s) => [s.id, s]));

  const visibleIds = new Set<string>();
  function addAncestors(spaceId: string) {
    if (visibleIds.has(spaceId)) return;
    visibleIds.add(spaceId);
    const space = spaceById.get(spaceId);
    if (space?.parentId) addAncestors(space.parentId);
  }
  taskSpaceIds.forEach(addAncestors);

  const nodeMap = new Map<string, TreeNode>();
  allSpaces.forEach((space) => {
    if (visibleIds.has(space.id)) {
      nodeMap.set(space.id, {
        space,
        children: [],
        hasDirectTask: taskSpaceIds.has(space.id),
        descendantTaskSpaces: [],
      });
    }
  });

  const roots: TreeNode[] = [];
  nodeMap.forEach((node) => {
    const parentId = node.space.parentId;
    if (parentId && nodeMap.has(parentId)) {
      nodeMap.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  function calculateDescendants(node: TreeNode): string[] {
    const ids: string[] = [];
    if (node.hasDirectTask) ids.push(node.space.id);
    for (const child of node.children) {
      ids.push(...calculateDescendants(child));
    }
    node.descendantTaskSpaces = ids;
    return ids;
  }
  roots.forEach(calculateDescendants);

  return roots;
}

export type CheckboxState = "checked" | "unchecked" | "indeterminate";

export function getCheckboxState(
  ids: string[],
  spaces: WorkSectionSpaceState[],
  field: SpaceInspectionField,
): CheckboxState {
  if (ids.length === 0) return "unchecked";
  const states = ids.map(
    (id) => spaces.find((s) => s.schedulingSpaceId === id)?.[field] ?? false,
  );
  if (states.every((s) => s)) return "checked";
  if (states.every((s) => !s)) return "unchecked";
  return "indeterminate";
}

interface SpaceNodeProps {
  node: TreeNode;
  rowIndex: number;
  depth: number;
  hasChildren: boolean;
  zebraIndex?: number;
  spaces: WorkSectionSpaceState[];
  availability: Availability;
  onUpdateSpace: (updates: SpaceUpdate[]) => void;
  collapsedSpaces: Set<string>;
  onToggleCollapse: (spaceId: string) => void;
  focusedRowIndex: number | null;
  focusedCellIndex: number | null;
  onFocusCell?: (row: number, cell: number) => void;
  displayOnly?: boolean;
  responsibleUsers?: ResponsibleUserOption[];
  responsibleBySpaceId?: Map<string, { userId: string; inherited: boolean }>;
  onSetSpaceResponsible?: (schedulingSpaceId: string, userId: string | null) => void;
}

function SpaceNode({
  node,
  rowIndex,
  depth,
  hasChildren,
  zebraIndex,
  spaces,
  availability,
  onUpdateSpace,
  collapsedSpaces,
  onToggleCollapse,
  focusedRowIndex,
  focusedCellIndex,
  onFocusCell,
  displayOnly = false,
  responsibleUsers,
  responsibleBySpaceId,
  onSetSpaceResponsible,
}: SpaceNodeProps) {
  const { isActive } = useColumnState();
  const expanded = !collapsedSpaces.has(node.space.id);
  const isFocusedRow = focusedRowIndex === rowIndex;
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFocusedRow && rowRef.current) {
      rowRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [isFocusedRow]);

  const handleParentToggle = useCallback(
    (field: SpaceInspectionField, currentState: CheckboxState, enabled: boolean) => {
      if (!enabled) return;
      const targetValue = currentState === "checked" ? false : true;
      onUpdateSpace(
        node.descendantTaskSpaces.map((id) => ({ spaceId: id, field, value: targetValue })),
      );
    },
    [node.descendantTaskSpaces, onUpdateSpace],
  );

  const isLeafWithTask = node.hasDirectTask && !hasChildren;
  const zebra =
    isLeafWithTask && zebraIndex !== undefined && zebraIndex % 2 === 1
      ? "bg-muted/20"
      : undefined;

  // When this column is active and this row is focused, each cell gets its own
  // background (solid blue for the focused cell, muted blue for the others).
  // When the column is inactive, the whole row gets the muted highlight so the
  // selection remains visible but doesn't compete with the active column.
  const isCellActive = isFocusedRow && isActive;

  function nameCellBg() {
    if (!isCellActive) return "";
    return focusedCellIndex === 0 ? "bg-primary" : "bg-primary/20";
  }

  return (
    <div
      ref={rowRef}
      className={cn(
        "flex items-stretch border-b",
        !isFocusedRow && isLeafWithTask && "hover:bg-muted/40",
        hasChildren && "cursor-pointer",
        !isFocusedRow && hasChildren && "hover:bg-muted/30",
        !isFocusedRow && zebra,
        isFocusedRow && !isActive && "bg-primary/20",
      )}
      onClick={() => {
        onFocusCell?.(rowIndex, 0);
        if (hasChildren) onToggleCollapse(node.space.id);
      }}
    >
      <div
        className={cn(
          "flex-1 flex items-center gap-2 py-1.5 pr-2 border-r min-w-[6rem]",
          nameCellBg(),
        )}
        style={{ paddingLeft: `${depth * 20 + 4}px` }}
      >
        <span
          className={cn(
            "shrink-0 w-5 h-5 flex items-center justify-center",
            isCellActive && focusedCellIndex === 0
              ? "text-primary-foreground/70"
              : "text-muted-foreground",
          )}
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )
          ) : null}
        </span>

        <span
          className={cn(
            "flex-1 text-xs truncate min-w-0",
            isCellActive && focusedCellIndex === 0
              ? "text-primary-foreground"
              : node.hasDirectTask
              ? "font-medium"
              : "text-muted-foreground",
          )}
        >
          {node.space.name}
        </span>
      </div>

      {FIELDS.map(({ field, availabilityKey }, colIdx) => {
        const cellIndex = colIdx + 1;
        const state = getCheckboxState(node.descendantTaskSpaces, spaces, field);
        const enabled = availability[availabilityKey];
        return (
          <CheckboxCell
            key={field}
            id={`${field}-${node.space.id}`}
            show={node.descendantTaskSpaces.length > 0}
            enabled={enabled}
            state={state}
            hasChildren={hasChildren}
            isFocused={isCellActive && focusedCellIndex === cellIndex}
            isRowFocused={isCellActive && focusedCellIndex !== cellIndex}
            isLastCell={colIdx === FIELDS.length - 1}
            onToggle={(e) => {
              e.stopPropagation();
              onFocusCell?.(rowIndex, cellIndex);
              handleParentToggle(field, state, enabled);
            }}
            displayOnly={displayOnly}
          />
        );
      })}

      {node.space.id !== PLOT_ROW_ID && (
        <ResponsibleCell
          schedulingSpaceId={node.space.id}
          effective={responsibleBySpaceId?.get(node.space.id)}
          users={responsibleUsers ?? []}
          onSet={onSetSpaceResponsible}
          displayOnly={displayOnly}
        />
      )}
      {node.space.id === PLOT_ROW_ID && <div className="w-24 shrink-0" />}
    </div>
  );
}

interface ResponsibleUserOption {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  substituteUserId?: string | null;
  substituteFirstName?: string | null;
  substituteLastName?: string | null;
}

function ResponsibleCell({
  schedulingSpaceId,
  effective,
  users,
  onSet,
  displayOnly,
}: {
  schedulingSpaceId: string;
  effective: { userId: string; inherited: boolean } | undefined;
  users: ResponsibleUserOption[];
  onSet?: (schedulingSpaceId: string, userId: string | null) => void;
  displayOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const user = effective ? users.find((u) => u.id === effective.userId) : undefined;
  const firstName = user ? (user.firstName?.trim() || user.email) : undefined;
  // Substitution-aware, kept compact: show the substitute's first name with the
  // original in parentheses ("B (A)"); the title carries the full names.
  const substituteFirst = user?.substituteUserId
    ? user.substituteFirstName?.trim()
    : undefined;
  const compactName = substituteFirst
    ? `${substituteFirst} (${firstName})`
    : firstName;

  const label = firstName ? (
    <span
      className={cn(
        "text-xs truncate",
        effective?.inherited ? "text-muted-foreground italic" : "",
      )}
      title={formatResponsible(user)}
    >
      {compactName}
    </span>
  ) : (
    <PersonPlusIcon className="h-4 w-4 text-muted-foreground" />
  );

  if (displayOnly || !onSet) {
    // Read-only / view mode (e.g. the project viewer's Tehtävät tab when not
    // editing): show the assigned or inherited name, but never the person-plus
    // affordance — assigning is only offered when the editor is interactive
    // (edit mode, and the init / mini-init flows, which pass `onSet`).
    return (
      <div className="w-24 shrink-0 flex items-center pl-2 py-1.5">
        {firstName ? label : null}
      </div>
    );
  }

  const filtered = users.filter((u) => {
    const t = `${u.firstName ?? ""} ${u.lastName ?? ""} ${u.email}`.toLowerCase();
    return t.includes(search.toLowerCase());
  });

  return (
    <div
      ref={containerRef}
      className="w-24 shrink-0 relative flex items-center pl-1 py-1"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-muted/60 min-w-0 w-full"
        title="Aseta vastuuhenkilö"
      >
        {label}
      </button>
      {open && (
        <div className="absolute z-50 top-full right-0 mt-1 w-48 rounded-md border bg-popover shadow-md">
          <div className="flex items-center border-b px-3">
            <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hae käyttäjää..."
              className="flex h-9 w-full bg-transparent py-2 text-base md:text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            <button
              type="button"
              onClick={() => {
                onSet(schedulingSpaceId, null);
                setOpen(false);
                setSearch("");
              }}
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground text-muted-foreground"
            >
              Peri työvaiheelta
            </button>
            {filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => {
                  onSet(schedulingSpaceId, u.id);
                  setOpen(false);
                  setSearch("");
                }}
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <CheckmarkIcon
                  className={cn(
                    "mr-2 h-4 w-4 shrink-0",
                    effective && !effective.inherited && effective.userId === u.id
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <span className="truncate text-left">
                  {`${u.firstName?.trim() ?? ""} ${u.lastName?.trim() ?? ""}`.trim() || u.email}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface CheckboxCellProps {
  id: string;
  show: boolean;
  enabled: boolean;
  state: CheckboxState;
  hasChildren: boolean;
  isFocused?: boolean;
  isRowFocused?: boolean;
  isLastCell?: boolean;
  onToggle: (e: React.MouseEvent) => void;
  displayOnly?: boolean;
}

function CheckboxCell({
  id,
  show,
  enabled,
  state,
  hasChildren,
  isFocused,
  isRowFocused,
  isLastCell,
  onToggle,
  displayOnly = false,
}: CheckboxCellProps) {
  const bg = isFocused ? "bg-primary" : isRowFocused ? "bg-primary/20" : "";
  const base = cn(
    COL_WIDTH,
    "shrink-0 flex justify-center items-center py-1.5",
    !isLastCell && "border-r",
    bg,
  );

  if (!show) {
    return (
      <div className={base}>
        <span className="w-4 h-4" />
      </div>
    );
  }

  if (displayOnly) {
    return (
      <div className={base}>
        <span className="opacity-50">
          <Checkbox
            checked={state === "checked"}
            className={cn(
              "pointer-events-none",
              isFocused && "border-primary-foreground data-[state=checked]:bg-primary-foreground data-[state=checked]:text-primary",
            )}
          />
        </span>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className={base}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="opacity-50 cursor-not-allowed">
              <Checkbox checked={false} disabled className="pointer-events-none" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{TOOLTIP_UNAVAILABLE}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={base}>
      <div className="flex items-center gap-2 relative cursor-pointer" onClick={onToggle}>
        <Checkbox
          id={id}
          checked={state === "checked"}
          className={cn(
            "pointer-events-none",
            isFocused &&
              "border-primary-foreground data-[state=checked]:bg-primary-foreground data-[state=checked]:text-primary",
          )}
        />
        {hasChildren && state === "indeterminate" && (
          <MinusIcon
            className={cn(
              "h-3 w-3 absolute left-0.5 top-1/2 -translate-y-1/2 pointer-events-none",
              isFocused ? "text-primary-foreground" : "text-muted-foreground",
            )}
          />
        )}
      </div>
    </div>
  );
}

export function WorkSectionSpaceTree({
  spaces,
  allSchedulingSpaces,
  plotName,
  availableAtWs,
  onUpdateSpace,
  collapsedSpaces: collapsedProp,
  onSetCollapsedSpaces: onSetCollapsedProp,
  focusedRowIndex = null,
  focusedCellIndex = null,
  onFocusCell,
  displayOnly = false,
  responsibleUsers,
  responsibleBySpaceId,
  onSetSpaceResponsible,
}: WorkSectionSpaceTreeProps) {
  const [internalCollapsed, setInternalCollapsed] = useState<Set<string>>(
    () => new Set(),
  );
  const collapsedSpaces = collapsedProp ?? internalCollapsed;
  const setCollapsedSpaces =
    onSetCollapsedProp ??
    ((updater: (prev: Set<string>) => Set<string>) =>
      setInternalCollapsed((prev) => updater(prev)));

  const onToggleCollapse = useCallback(
    (spaceId: string) => {
      setCollapsedSpaces((prev) => {
        const next = new Set(prev);
        if (next.has(spaceId)) next.delete(spaceId);
        else next.add(spaceId);
        return next;
      });
    },
    [setCollapsedSpaces],
  );

  const { flatRows, availability } = useFlatTreeModel({
    spaces,
    allSchedulingSpaces,
    availableAtWs,
    collapsedSpaces,
    plotName,
  });

  // Pre-compute zebra index per visible leaf-with-task row (no mutable
  // counters during render — that's broken under React StrictMode, which
  // double-invokes function-component bodies in development).
  const zebraIndexByKey = useMemo(() => {
    const map = new Map<string, number>();
    let i = 0;
    for (const row of flatRows) {
      if (
        row.kind === "space" &&
        row.node.hasDirectTask &&
        !row.hasChildren
      ) {
        map.set(row.key, i);
        i += 1;
      }
    }
    return map;
  }, [flatRows]);

  const handleBulkToggle = (field: SpaceInspectionField, toValue: boolean) => {
    onUpdateSpace(
      spaces.map((s) => ({ spaceId: s.schedulingSpaceId, field, value: toValue })),
    );
  };

  const { isActive } = useColumnState();
  const usePlotRow = plotName !== undefined;
  const isHeaderFocused = !usePlotRow && focusedRowIndex === 0;
  const hasTreeRows = flatRows.some((r) => r.kind === "space");
  const headerFocusBg = isHeaderFocused
    ? isActive
      ? "bg-primary/15"
      : "bg-primary/5"
    : null;

  return (
    <TooltipProvider delayDuration={150}>
      {usePlotRow ? (
        // Labels-only header — bulk toggling lives on the synthetic plot row.
        <div className="flex items-end pb-2 border-b text-muted-foreground">
          <div className="flex-1 flex items-end gap-2 pr-2 min-w-0" style={{ paddingLeft: "4px" }}>
            <span className="w-5 shrink-0" />
            <span className="flex-1 text-xs font-medium">Tila</span>
          </div>
          {FIELDS.map(({ field, label }) => (
            <div
              key={field}
              className={cn(
                COL_WIDTH,
                "shrink-0 flex items-end justify-center py-1",
              )}
            >
              <span className="text-[9px] font-medium leading-tight text-center break-words w-full">
                {label}
              </span>
            </div>
          ))}
          <div className="w-24 shrink-0 flex items-end justify-start py-1 pl-2">
            <span className="text-[9px] font-medium leading-tight">
              Tilaspesifi vastuuhenkilö
            </span>
          </div>
        </div>
      ) : (
        // Legacy header row — each column header carries its own bulk-toggle
        // checkbox, and the row participates in keyboard navigation.
        <div
          className={cn(
            "flex items-end gap-2 px-1 pb-2 border-b text-muted-foreground rounded",
            headerFocusBg,
          )}
        >
          <div
            className={cn(
              "flex-1 flex items-end gap-2 rounded px-1 min-w-0",
              isHeaderFocused &&
                focusedCellIndex === 0 &&
                "ring-2 ring-primary ring-inset",
            )}
          >
            <span className="w-5 shrink-0" />
            <span className="flex-1 text-xs font-medium">Tila</span>
          </div>
          {FIELDS.map(({ field, label, availabilityKey }, idx) => {
            const cellIndex = idx + 1;
            const enabled = availability[availabilityKey];
            const selected = spaces.reduce((n, s) => n + (s[field] ? 1 : 0), 0);
            const state: CheckboxState =
              !enabled || selected === 0
                ? "unchecked"
                : selected === spaces.length
                ? "checked"
                : "indeterminate";
            const isCellFocused =
              isHeaderFocused && focusedCellIndex === cellIndex;
            return (
              <div
                key={field}
                className={cn(
                  COL_WIDTH,
                  "shrink-0 flex flex-col items-center gap-1 rounded py-1",
                  isCellFocused && "ring-2 ring-primary ring-inset",
                )}
              >
                <span className="text-[9px] font-medium leading-tight text-center break-words w-full">
                  {label}
                </span>
                <div
                  className={cn(
                    "relative",
                    enabled ? "cursor-pointer" : "cursor-not-allowed opacity-50",
                  )}
                  onClick={
                    enabled
                      ? () => {
                          onFocusCell?.(0, cellIndex);
                          handleBulkToggle(field, state !== "checked");
                        }
                      : undefined
                  }
                >
                  <Checkbox
                    id={`bulk-${field}`}
                    checked={state === "checked"}
                    disabled={!enabled}
                    className="pointer-events-none"
                  />
                  {enabled && state === "indeterminate" && (
                    <MinusIcon className="h-3 w-3 text-muted-foreground absolute left-0.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  )}
                </div>
              </div>
            );
          })}
          <div className="w-24 shrink-0 flex items-end justify-start py-1 pl-2">
            <span className="text-[9px] font-medium leading-tight">
              Vastuuhenkilö
            </span>
          </div>
        </div>
      )}

      {/* Tree content — iterate the space rows (skip the legacy header). */}
      {!hasTreeRows ? (
        <p className="text-sm text-muted-foreground py-2">
          Valitulla työvaiheella ei ole ajoitettuja tiloja
        </p>
      ) : (
        <div className="pt-1">
          {flatRows.map((row, rowIndex) =>
            row.kind === "space" ? (
              <SpaceNode
                key={row.key}
                node={row.node}
                rowIndex={rowIndex}
                depth={row.depth}
                hasChildren={row.hasChildren}
                zebraIndex={zebraIndexByKey.get(row.key)}
                spaces={spaces}
                availability={availability}
                onUpdateSpace={onUpdateSpace}
                collapsedSpaces={collapsedSpaces}
                onToggleCollapse={onToggleCollapse}
                focusedRowIndex={focusedRowIndex}
                focusedCellIndex={focusedCellIndex}
                onFocusCell={onFocusCell}
                displayOnly={displayOnly}
                responsibleUsers={responsibleUsers}
                responsibleBySpaceId={responsibleBySpaceId}
                onSetSpaceResponsible={onSetSpaceResponsible}
              />
            ) : null,
          )}
        </div>
      )}
    </TooltipProvider>
  );
}

// ─── Flat tree model (for keyboard navigation) ────────────────────────────────

/** One row of the flattened tree in DFS render order. The first row depends
 *  on whether a plot name was supplied: with a plot name, row 0 is the
 *  synthetic plot row (kind "space", depth 0); without one, row 0 is the
 *  legacy header row (kind "header"). */
export type FlatRow =
  | { kind: "header"; key: string }
  | { kind: "space"; key: string; node: TreeNode; depth: number; hasChildren: boolean };

/** Synthetic id for the plot row injected at the top of every tree. The
 *  collapsed-state set stores this when the user collapses the plot row. */
export const PLOT_ROW_ID = "__plot__";

/** Produce the same flat row ordering the tree renders, plus availability,
 *  so a parent can build keyboard navigation that maps row/cell indices back
 *  to actions. The result is memoised on its inputs. */
export function useFlatTreeModel({
  spaces,
  allSchedulingSpaces,
  availableAtWs,
  collapsedSpaces,
  plotName,
}: {
  spaces: WorkSectionSpaceState[];
  allSchedulingSpaces: SchedulingSpace[];
  availableAtWs?: Set<string>;
  collapsedSpaces: Set<string>;
  plotName?: string;
}): { flatRows: FlatRow[]; availability: Availability } {
  const availability: Availability = useMemo(
    () => ({
      malliasennus: availableAtWs?.has("Malliasennustarkastus") ?? false,
      osakohde: availableAtWs?.has("Osakohteen tarkastus") ?? false,
      mestanVastaanotto: availableAtWs?.has("Mestan vastaanotto") ?? false,
      tyovaiheenvastaanotto:
        availableAtWs?.has("Työvaiheenvastaanotto") ?? false,
      liitedokumentit: availableAtWs?.has("Liitedokumentit") ?? false,
    }),
    [availableAtWs],
  );

  const flatRows = useMemo(() => {
    const taskSpaceIds = new Set(spaces.map((s) => s.schedulingSpaceId));
    const scopedSpaces = stripPlotSpaces(allSchedulingSpaces);
    const roots = buildVisibleTree(taskSpaceIds, scopedSpaces);
    const startByTaskSpace = new Map<string, string>();
    const endByTaskSpace = new Map<string, string>();
    for (const s of spaces) {
      if (s.plannedStart)
        startByTaskSpace.set(s.schedulingSpaceId, s.plannedStart);
      if (s.plannedEnd) endByTaskSpace.set(s.schedulingSpaceId, s.plannedEnd);
    }
    const tree = sortTreeByScheduling(roots, startByTaskSpace, endByTaskSpace);

    function walk(node: TreeNode, depth: number, rows: FlatRow[]) {
      const hasChildren = node.children.length > 0;
      rows.push({
        kind: "space",
        key: node.space.id,
        node,
        depth,
        hasChildren,
      });
      if (hasChildren && !collapsedSpaces.has(node.space.id)) {
        for (const child of node.children) walk(child, depth + 1, rows);
      }
    }

    if (plotName !== undefined) {
      const rows: FlatRow[] = [];
      if (tree.length === 0) return rows;
      const plotNode: TreeNode = {
        space: {
          id: PLOT_ROW_ID,
          name: plotName,
          parentId: null,
          type: null,
        },
        children: tree,
        hasDirectTask: false,
        descendantTaskSpaces: tree.flatMap((r) => r.descendantTaskSpaces),
      };
      walk(plotNode, 0, rows);
      return rows;
    }

    const rows: FlatRow[] = [{ kind: "header", key: "__header__" }];
    for (const root of tree) walk(root, 0, rows);
    return rows;
  }, [spaces, allSchedulingSpaces, collapsedSpaces, plotName]);

  return { flatRows, availability };
}
